import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, Book, ShieldOff, AlertTriangle } from 'react-feather';

const TermsOfServicePage = () => {
  const navigate = useNavigate();
  
  const navigateTo = (path: string) => {
    navigate(path);
  };
  
  return (
    <div className="bg-white min-h-screen">
      {/* Header */}
      <header className="bg-gradient-to-r from-pink-500 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl">
              Terms of Service
            </h1>
            <p className="mt-6 max-w-3xl mx-auto text-xl">
              Please read these terms carefully before using our services.
            </p>
          </motion.div>
        </div>
      </header>

      {/* Terms of Service Content */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="prose prose-lg max-w-none"
          >
            <div className="mb-12">
              <div className="flex items-center mb-4">
                <FileText className="h-8 w-8 text-pink-500 mr-3" />
                <h2 className="text-3xl font-bold text-gray-900 m-0">Introduction</h2>
              </div>
              <p>Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
              <p>
                Welcome to Wedding App! These Terms of Service ("Terms") govern your access to and use of the Wedding App website and services, including any content, functionality, and services offered on or through our website or mobile applications (the "Service").
              </p>
              <p>
                Please read these Terms carefully before you start to use the Service. By using the Service, or by clicking to accept or agree to the Terms when this option is made available to you, you accept and agree to be bound and abide by these Terms of Service. If you do not want to agree to these Terms of Service, you must not access or use the Service.
              </p>
            </div>

            <div className="mb-12">
              <div className="flex items-center mb-4">
                <Book className="h-8 w-8 text-pink-500 mr-3" />
                <h2 className="text-3xl font-bold text-gray-900 m-0">Service Description</h2>
              </div>
              <p>
                Wedding App provides an online platform that allows users to create and manage wedding websites, guest lists, registries, and other wedding planning tools ("Service"). The Service is made available to you on the condition that you agree to these Terms.
              </p>
              <h3 className="text-xl font-semibold">Account Registration</h3>
              <p>
                To access certain features of the Service, you may be required to register for an account. You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete.
              </p>
              <p>
                You are responsible for safeguarding your password and for all activities that occur under your account. You agree to immediately notify Wedding App of any unauthorized use of your account or any other breach of security.
              </p>
            </div>

            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">User Content</h2>
              <p>
                Our Service allows you to post, link, store, share and otherwise make available certain information, text, graphics, videos, or other material ("User Content"). You are responsible for the User Content that you post on or through the Service, including its legality, reliability, and appropriateness.
              </p>
              <p>
                By posting User Content on or through the Service, you represent and warrant that:
              </p>
              <ul>
                <li>The User Content is yours or you have the right to use it and grant us the rights and license as provided in these Terms.</li>
                <li>The posting of your User Content on or through the Service does not violate the privacy rights, publicity rights, copyrights, contract rights or any other rights of any person or entity.</li>
              </ul>
              <p>
                We reserve the right to remove any User Content from the Service at our discretion, without prior notice, for any reason, including if we believe that such content violates these Terms or is otherwise objectionable or harmful to the Service or to other users.
              </p>
              <p>
                You retain all of your ownership rights in your User Content. However, by posting User Content on or through the Service, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, adapt, publish, translate, create derivative works from, distribute, and display such content in any media.
              </p>
            </div>

            <div className="mb-12">
              <div className="flex items-center mb-4">
                <ShieldOff className="h-8 w-8 text-pink-500 mr-3" />
                <h2 className="text-3xl font-bold text-gray-900 m-0">Prohibited Uses</h2>
              </div>
              <p>
                You may use the Service only for lawful purposes and in accordance with these Terms. You agree not to use the Service:
              </p>
              <ul>
                <li>In any way that violates any applicable federal, state, local, or international law or regulation.</li>
                <li>To transmit, or procure the sending of, any advertising or promotional material, including any "junk mail," "chain letter," "spam," or any other similar solicitation.</li>
                <li>To impersonate or attempt to impersonate Wedding App, a Wedding App employee, another user, or any other person or entity.</li>
                <li>To engage in any other conduct that restricts or inhibits anyone's use or enjoyment of the Service, or which may harm Wedding App or users of the Service, or expose them to liability.</li>
                <li>To upload or transmit viruses, Trojan horses, or other material that is malicious or technologically harmful.</li>
              </ul>
            </div>
            
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Payment Terms</h2>
              <p>
                Some aspects of the Service may be provided for a fee or other charge. If you elect to use paid aspects of the Service, you agree to the pricing and payment terms as we may update them from time to time.
              </p>
              <p>
                All payments are non-refundable except as expressly set forth in these Terms or when required by law. We may change the fees and charges in effect, or add new fees and charges from time to time, but we will give you advance notice of these changes.
              </p>
              <p>
                You are responsible for paying all fees and taxes associated with your use of the Service. Payment obligations are non-cancelable, and fees paid are non-refundable, and you will not receive any refund for any unused subscription upon termination.
              </p>
            </div>
            
            <div className="mb-12">
              <div className="flex items-center mb-4">
                <AlertTriangle className="h-8 w-8 text-pink-500 mr-3" />
                <h2 className="text-3xl font-bold text-gray-900 m-0">Disclaimers and Limitations of Liability</h2>
              </div>
              <p>
                The Service and all content is provided "as is" and "as available" without any warranty of any kind, either express or implied, including but not limited to the implied warranties of merchantability, fitness for a particular purpose, or non-infringement.
              </p>
              <p>
                Wedding App does not warrant that the Service will be uninterrupted or error-free, that defects will be corrected, or that the Service or the server that makes it available are free of viruses or other harmful components.
              </p>
              <p>
                In no event will Wedding App, its affiliates, or their licensors, service providers, employees, agents, officers, or directors be liable for damages of any kind, under any legal theory, arising out of or in connection with your use of the Service, including but not limited to personal injury, pain and suffering, emotional distress, loss of revenue, loss of profits, loss of business or anticipated savings, loss of use, loss of goodwill, loss of data, and whether caused by tort (including negligence), breach of contract, or otherwise, even if foreseeable.
              </p>
            </div>
            
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Termination</h2>
              <p>
                We may terminate or suspend your account and bar access to the Service immediately, without prior notice or liability, under our sole discretion, for any reason whatsoever and without limitation, including but not limited to a breach of the Terms.
              </p>
              <p>
                If you wish to terminate your account, you may simply discontinue using the Service, or notify us that you wish to delete your account. All provisions of the Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
              </p>
            </div>
            
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Changes to Terms</h2>
              <p>
                We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
              </p>
              <p>
                By continuing to access or use our Service after any revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, you are no longer authorized to use the Service.
              </p>
            </div>
            
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Contact Us</h2>
              <p>
                If you have any questions about these Terms, please contact us at:
              </p>
              <p>
                <strong>Email:</strong> legal@weddingapp.com<br />
                <strong>Address:</strong> 123 Wedding Avenue, New York, NY 10001<br />
                <strong>Phone:</strong> +1 (555) 123-4567
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400">© {new Date().getFullYear()} Wedding App. All rights reserved.</p>
          <div className="mt-4">
            <button 
              onClick={() => navigateTo('/')}
              className="text-gray-400 hover:text-pink-300 transition-colors mx-2"
            >
              Home
            </button>
            <button 
              onClick={() => navigateTo('/about')}
              className="text-gray-400 hover:text-pink-300 transition-colors mx-2"
            >
              About Us
            </button>
            <button 
              onClick={() => navigateTo('/contact')}
              className="text-gray-400 hover:text-pink-300 transition-colors mx-2"
            >
              Contact
            </button>
            <button 
              onClick={() => navigateTo('/privacy')}
              className="text-gray-400 hover:text-pink-300 transition-colors mx-2"
            >
              Privacy Policy
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfServicePage;